import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../contexts/NotificationContext';
import { usePageLoaderContext } from '../../../contexts/PageLoaderContext';
import { useConfirmService } from '../../../services/confirmService';
import apiService from '../../../services/api';
import CustomDropdown, { DropdownOption } from '../../UI/CustomDropdown';
import ConfirmModal from '../../Notification/ConfirmModal';
import { PermissionGroup, PermissionGroupFormData } from './types';
import PermissionGroupList from './components/PermissionGroupList';
import AddEditPermissionGroupPanel from './components/AddEditPermissionGroupPanel';
import styles from '../../../styles/PermissionManagement.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import customDropdownStyles from '../../../styles/CustomDropdown.module.css';

interface FilterState {
  is_active: string;
}

const PermissionManagement: React.FC = () => {
  const notificationService = useNotification();
  const { showLoader, hideLoader } = usePageLoaderContext();
  const confirmService = useConfirmService();

  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [showAddGroupPanel, setShowAddGroupPanel] = useState(false);
  const [editingGroup, setEditingGroup] = useState<PermissionGroup | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    is_active: ''
  });
  const [formData, setFormData] = useState<PermissionGroupFormData>({
    name: '',
    description: '',
    is_active: true,
    permissions: []
  });

  const statusOptions: DropdownOption[] = [
    { value: '', label: 'All Status' },
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' }
  ];

  useEffect(() => {
    loadAllPermissions();
    const timeoutId = setTimeout(() => {
      loadPermissionGroups();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [filters.is_active]);

  const loadAllPermissions = async () => {
    try {
      const response = await apiService.getAllPermissions();
      if (response.status === 'success') {
        setAllPermissions((response as any).permissions || []);
      }
    } catch (error) {
      console.error('Error loading all permissions:', error);
    }
  };

  const loadPermissionGroups = async () => {
    if (isLoadingGroups) return;

    setIsLoadingGroups(true);
    setLoading(true);
    showLoader('Loading permission groups...');

    try {
      const params: any = {};
      if (filters.is_active) params.is_active = filters.is_active;

      const response = await apiService.getPermissionGroups(params);

      if (response.status === 'success') {
        setGroups((response as any).permission_groups || []);
      } else {
        throw new Error(response.message || 'Failed to load permission groups');
      }

    } catch (error: any) {
      console.error('Error loading permission groups:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load permission groups. Please try again.');
    } finally {
      setLoading(false);
      setIsLoadingGroups(false);
      hideLoader();
    }
  };

  const handleAddGroup = async () => {
    showLoader('Creating permission group...');
    try {
      const response = await apiService.createPermissionGroup({
        name: formData.name,
        description: formData.description,
        is_active: formData.is_active,
        permissions: formData.permissions
      });

      if (response.status === 'success') {
        notificationService.success('Permission group created successfully');
        setShowAddGroupPanel(false);
        resetForm();
        loadPermissionGroups();
      } else {
        throw new Error(response.message || 'Failed to create permission group');
      }
    } catch (error: any) {
      console.error('Error creating permission group:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to create permission group. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleEditGroup = async () => {
    if (!editingGroup) return;

    showLoader('Updating permission group...');
    try {
      const response = await apiService.updatePermissionGroup(editingGroup.id, {
        name: formData.name,
        description: formData.description,
        is_active: formData.is_active,
        permissions: formData.permissions
      });

      if (response.status === 'success') {
        notificationService.success('Permission group updated successfully');
        setShowAddGroupPanel(false);
        setEditingGroup(null);
        resetForm();
        loadPermissionGroups();
      } else {
        throw new Error(response.message || 'Failed to update permission group');
      }
    } catch (error: any) {
      console.error('Error updating permission group:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update permission group. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleTogglePermission = async (groupId: string, permissionId: string, isCurrentlyActive: boolean) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    // Get current permission IDs
    const currentPermissionIds = group.permissions.map(p => p.id);

    // Optimistic update - update UI immediately
    let newPermissionIds: string[];
    if (isCurrentlyActive) {
      // Remove permission
      newPermissionIds = currentPermissionIds.filter(id => id !== permissionId);
    } else {
      // Add permission
      newPermissionIds = [...currentPermissionIds, permissionId];
    }

    setGroups(prevGroups =>
      prevGroups.map(g => {
        if (g.id === groupId) {
          const updatedPermissions = allPermissions.filter(p => newPermissionIds.includes(p.id));
          return {
            ...g,
            permissions: updatedPermissions,
            permissions_count: newPermissionIds.length
          };
        }
        return g;
      })
    );

    showLoader('Updating permission...');
    try {
      const response = await apiService.updatePermissionGroup(groupId, {
        name: group.name,
        description: group.description,
        is_active: group.is_active,
        permissions: newPermissionIds
      });

      if (response.status === 'success') {
        notificationService.success(`Permission ${isCurrentlyActive ? 'removed' : 'added'} successfully`);
      } else {
        // Revert on failure
        setGroups(prevGroups =>
          prevGroups.map(g =>
            g.id === groupId ? group : g
          )
        );
        throw new Error(response.message || 'Failed to update permission');
      }
    } catch (error: any) {
      console.error('Error toggling permission:', error);
      // Revert on error
      setGroups(prevGroups =>
        prevGroups.map(g =>
          g.id === groupId ? group : g
        )
      );
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update permission. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleToggleStatus = async (groupId: string, currentStatus: boolean) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    // Optimistic update - update UI immediately
    const newStatus = !currentStatus;
    setGroups(prevGroups =>
      prevGroups.map(g =>
        g.id === groupId ? { ...g, is_active: newStatus } : g
      )
    );

    showLoader('Updating status...');
    try {
      const response = await apiService.updatePermissionGroup(groupId, {
        name: group.name,
        description: group.description,
        is_active: newStatus,
        permissions: group.permissions.map(p => p.id)
      });

      if (response.status === 'success') {
        notificationService.success(`Permission group ${newStatus ? 'activated' : 'deactivated'} successfully`);
      } else {
        // Revert on failure
        setGroups(prevGroups =>
          prevGroups.map(g =>
            g.id === groupId ? { ...g, is_active: currentStatus } : g
          )
        );
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (error: any) {
      console.error('Error toggling status:', error);
      // Revert on error
      setGroups(prevGroups =>
        prevGroups.map(g =>
          g.id === groupId ? { ...g, is_active: currentStatus } : g
        )
      );
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to update status. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    const confirmed = await confirmService.show(
      'Delete Permission Group',
      `Are you sure you want to delete "${groupName}"? This action cannot be undone.`,
      { okText: 'Delete', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    showLoader('Deleting permission group...');
    try {
      const response = await apiService.deletePermissionGroup(groupId);

      if (response.status === 'success') {
        notificationService.success('Permission group deleted successfully');
        loadPermissionGroups();
      } else {
        throw new Error(response.message || 'Failed to delete permission group');
      }
    } catch (error: any) {
      console.error('Error deleting permission group:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to delete permission group. Please try again.');
    } finally {
      hideLoader();
    }
  };

  const handleEdit = (group: PermissionGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description,
      is_active: group.is_active,
      permissions: group.permissions.map(p => p.id)
    });
    setShowAddGroupPanel(true);
  };

  const handleAddNew = () => {
    setEditingGroup(null);
    resetForm();
    setShowAddGroupPanel(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      is_active: true,
      permissions: []
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    if (editingGroup) {
      handleEditGroup();
    } else {
      handleAddGroup();
    }
  };

  const updateFilters = (field: keyof FilterState, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className={styles.permissionManagementContainer}>
      <div className={styles.fixedHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerRow}>
            <div className={styles.headerActions}>
              <CustomDropdown
                options={statusOptions}
                value={filters.is_active}
                onChange={(value) => updateFilters('is_active', Array.isArray(value) ? value[0] : value)}
                placeholder="All Status"
                className="filter"
              />
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                onClick={loadPermissionGroups}
                title="Refresh"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 2v6h-6"></path>
                  <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                  <path d="M3 22v-6h6"></path>
                  <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                </svg>
                <span className={styles.btnText}>Refresh</span>
              </button>
              <button
                className={`${buttonStyles.btn} ${buttonStyles.btnPrimary}`}
                onClick={handleAddNew}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                <span className={styles.btnText}>Add Permission Group</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.contentSection}>
        {loading ? (
          <div className={styles.skeletonContainer}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className={styles.permissionGroupCardSkeleton}>
                <div className={styles.skeletonSectionBadge}></div>
                <div className={styles.skeletonCardHeader}>
                  <div className={styles.skeletonSectionTitle}>
                    <div className={styles.skeletonIcon}></div>
                    <div className={styles.skeletonTitle}></div>
                  </div>
                  <div className={styles.skeletonDescription}></div>
                </div>
                <div className={styles.skeletonCardBody}>
                  <div className={styles.skeletonStats}>
                    <div className={styles.skeletonStatItem}></div>
                    <div className={styles.skeletonStatDivider}></div>
                    <div className={styles.skeletonStatItem}></div>
                    <div className={styles.skeletonStatDivider}></div>
                    <div className={styles.skeletonStatItem}></div>
                  </div>
                  <div className={styles.skeletonPermissionsList}>
                    <div className={styles.skeletonPermissionModule}></div>
                    <div className={styles.skeletonPermissionModule}></div>
                  </div>
                  <div className={styles.skeletonCardFooter}>
                    <div className={styles.skeletonMetadata}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <PermissionGroupList
            groups={groups}
            allPermissions={allPermissions}
            onEdit={handleEdit}
            onDelete={handleDeleteGroup}
            onToggleStatus={handleToggleStatus}
            onTogglePermission={handleTogglePermission}
          />
        )}
      </div>

      <AddEditPermissionGroupPanel
        isOpen={showAddGroupPanel}
        editingGroup={editingGroup}
        formData={formData}
        onClose={() => {
          setShowAddGroupPanel(false);
          setEditingGroup(null);
          resetForm();
        }}
        onSubmit={handleSubmit}
        onInputChange={handleInputChange}
      />

      <ConfirmModal
        isOpen={confirmService.isOpen}
        title={confirmService.title}
        message={confirmService.message}
        okText={confirmService.options.okText}
        cancelText={confirmService.options.cancelText}
        onConfirm={confirmService.onConfirm}
        onCancel={confirmService.onCancel}
      />
    </div>
  );
};

export default PermissionManagement;

